import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const ROLE_OPTIONS = [
  { value: 'artist', label: 'Artist', desc: 'I create and sell original works' },
  { value: 'collector', label: 'Collector', desc: 'I collect and curate art' },
  { value: 'gallery', label: 'Gallery', desc: 'I represent artists and sell art' },
  { value: 'dealer', label: 'Dealer', desc: 'I trade art and collectibles' },
  { value: 'advisor', label: 'Art Advisor', desc: 'I advise on acquisitions' },
];

export default function ProfileComplete() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || '',
    profile_type: user?.profile_type || '',
    bio: user?.bio || '',
    location: user?.location || '',
    website: user?.website || '',
    phone: user?.phone || '',
    portfolio_url: user?.portfolio_url || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.profile_type) { toast.error('Please select your role'); return; }
    setLoading(true);
    try {
      await api.put('/profile', form);
      const updated = await refreshUser();
      toast.success('Profile saved!');
      if (updated.membership_status === 'none') navigate('/membership/apply');
      else navigate('/membership/status');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: 'var(--bg-subtle)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container-app py-10">
        <div className="max-w-xl mx-auto">
          <div className="mb-8">
            <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--gold)' }}>Step 1 of 2</p>
            <h1 className="text-2xl lg:text-3xl font-semibold mb-2" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Complete your profile</h1>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Tell us about yourself so our team can review your membership application.</p>
          </div>

          <div className="p-8 rounded-2xl border" style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Role */}
              <div>
                <Label className="text-xs font-medium mb-3 block" style={{ color: 'var(--text-muted)' }}>I am a <span style={{ color: 'var(--gold)' }}>*</span></Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="profile-role-radio">
                  {ROLE_OPTIONS.map(r => (
                    <button key={r.value} type="button" onClick={() => setForm(f => ({...f, profile_type: r.value}))}
                      className="p-3 rounded-xl border text-left transition-colors"
                      style={{
                        borderColor: form.profile_type === r.value ? 'var(--gold)' : 'var(--border-clr)',
                        backgroundColor: form.profile_type === r.value ? 'var(--gold-pale)' : 'var(--surface)',
                        cursor: 'pointer',
                      }}>
                      <p className="text-sm font-medium" style={{ color: form.profile_type === r.value ? 'var(--text)' : 'var(--text-muted)' }}>{r.label}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)', fontSize: '10px' }}>{r.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Full Name</Label>
                <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} style={{ borderColor: 'var(--border-clr)' }} />
              </div>

              <div>
                <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Bio</Label>
                <Textarea placeholder="Tell us about your background in art..." value={form.bio} onChange={e => setForm(f => ({...f, bio: e.target.value}))} rows={3} style={{ borderColor: 'var(--border-clr)' }} />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Location</Label>
                  <Input placeholder="City, Country" value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} style={{ borderColor: 'var(--border-clr)' }} />
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Phone</Label>
                  <Input placeholder="+44 7700 000000" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} style={{ borderColor: 'var(--border-clr)' }} />
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Website</Label>
                <Input placeholder="https://yourgallery.com" value={form.website} onChange={e => setForm(f => ({...f, website: e.target.value}))} style={{ borderColor: 'var(--border-clr)' }} />
              </div>
              <div>
                <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Portfolio URL</Label>
                <Input placeholder="https://yourportfolio.com" value={form.portfolio_url} onChange={e => setForm(f => ({...f, portfolio_url: e.target.value}))} style={{ borderColor: 'var(--border-clr)' }} />
              </div>

              <Button type="submit" className="w-full" disabled={loading} data-testid="profile-complete-submit-button" style={{ backgroundColor: 'var(--text)', color: 'white', borderRadius: '10px' }}>
                {loading ? 'Saving...' : 'Save & Continue'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
